import { deriveDisplayEntries, type UtilityMeasurementEntry } from '@civilgrid/utils/src/geoItems/utilityMeasurements';
import type { GeoItem } from '~/common/types';
import { DEPRECATED_LONGITUDE_LATITUDE_PROP_LABEL, LONGITUDE_LATITUDE_PROP_LABEL } from './potholeProps';

const ARRAY_FIELDS = new Set(['utility', 'targetUtility']);

/**
 * Seeds form defaults from a GeoItem's props. Kept dependency-light (no UI
 * imports) so it can be unit-tested in the node test environment.
 */
export function getInitialValues({ geoItem, fieldMap }: { geoItem: GeoItem; fieldMap: { [key: string]: string } }) {
  const acc: { [key: string]: string | string[] | UtilityMeasurementEntry[] } = {};

  for (const key in fieldMap) {
    const v = fieldMap[key];
    // Prop labels are the DB lookup key. Fall back to the deprecated slash label
    // only when the canonical comma label is absent — mobile/PDF always use comma.
    let fieldValue = geoItem.fields?.[v]?.value;
    if (fieldValue == null && v === LONGITUDE_LATITUDE_PROP_LABEL) {
      fieldValue = geoItem.fields?.[DEPRECATED_LONGITUDE_LATITUDE_PROP_LABEL]?.value;
    }
    if (ARRAY_FIELDS.has(key)) {
      // Normalize legacy single-string values into a single-item array so the
      // form's array-backed fields never receive a bare string (which downstream
      // includes()/spread would corrupt char-by-char).
      acc[key] = Array.isArray(fieldValue) ? fieldValue : fieldValue == null ? [] : [fieldValue];
    } else {
      acc[key] = Array.isArray(fieldValue) ? fieldValue.join(', ') : (fieldValue ?? '');
    }
  }

  // Seed the per-utility cards: migrate legacy single values into one entry per
  // selected utility, and reconcile a legacy-client edit into entry[0]. Empty
  // for manholes (no Utility prop) — the extra key is harmless there.
  acc.utilityMeasurements = deriveDisplayEntries(geoItem.props);

  return acc;
}
